/**
 * Experiments Configuration
 * 
 * This file contains all active A/B tests and experiments.
 * Add new experiments here and they'll be automatically tracked.
 */

type ExperimentStatus = 'planned' | 'draft' | 'active' | 'paused' | 'completed' | 'archived';

type ExperimentConfig = {
  name: string;
  featureFlag?: string;
  description: string;
  variants?: Record<string, string>;
  metrics?: string[];
  posthog_metrics?: {
    primary?: string | string[];
    secondary?: string[];
  };
  primaryMetric?: string;
  secondaryMetrics?: string[];
  startDate: string;
  endDate?: string;
  status: ExperimentStatus;
  type?: string;
  hypothesis?: string;
  target_lift?: Record<string, number>;
  sample_size_needed?: string;
  notes?: string;
  result?: string;
  posthog_experiment_id?: number;
};

type ExperimentRegistry = Record<string, ExperimentConfig>;

// Active Experiments Registry
export const ACTIVE_EXPERIMENTS: ExperimentRegistry = {
  EDUCATION_CONTROL_ROOM_DESIGN_2026_08: {
    name: 'education_control_room_design_2026_08',
    featureFlag: 'education-control-room-design-2026-08',
    description: 'Compare current Education destination designs with one shared Control Room-inspired visual system while preserving content, data, actions, pricing, and route behavior.',
    variants: {
      control: 'Current destination designs',
      control_room: 'Shared Control Room Ledger design',
    },
    metrics: [
      'PaymentReceived',
      'BootcampCheckoutStarted',
      'pro_upgrade_cta_clicked',
      'ComplexBacktestCaseStudyCtaClick',
    ],
    posthog_metrics: {
      primary: 'Education and Pro PaymentReceived per exposed user',
      secondary: [
        'BootcampCheckoutStarted per exposed user',
        'Pro upgrade CTA clicks per exposed user',
        'ComplexBacktestCaseStudyCtaClick per exposed user',
      ],
    },
    primaryMetric: 'Education and Pro PaymentReceived per exposed user',
    secondaryMetrics: [
      'BootcampCheckoutStarted per exposed user',
      'Pro upgrade CTA clicks per exposed user',
      'ComplexBacktestCaseStudyCtaClick per exposed user',
    ],
    startDate: '2026-08-10',
    status: 'active',
    type: 'posthog_experiment',
    hypothesis: 'A coherent Control Room-inspired design across Education destinations will increase qualified progression into paid education and Pro without changing the underlying offers.',
    notes: 'Running in PostHog from 2026-08-10 with one 50/50 assignment across AI Bootcamp, Live Algo Lab, Challenges, Trading Competition, Case Studies, and Blog. Primary PaymentReceived metric includes only ai-bootcamp, pro-plan, and all-access-plan. Session assignment persists across login remounts. AI course-name experiment remains inactive and depends on this design flag being false. Local preview: ?educationDesign=control|control_room.',
    posthog_experiment_id: 411183,
  },

  AI_TRADING_BOOTCAMP_COURSE_NAME: {
    name: 'ai_trading_bootcamp_course_name',
    featureFlag: 'ai-trading-bootcamp-course-name',
    description: 'Compare AI Trading Bootcamp with Agentic Trading Bootcamp while holding the promise, page, curriculum, price, and calls to action constant.',
    variants: {
      control: 'AI Trading Bootcamp',
      agentic: 'Agentic Trading Bootcamp',
    },
    metrics: ['BootcampCheckoutStarted', 'PaymentReceived', 'BootcampPathSelected'],
    posthog_metrics: {
      primary: 'BootcampCheckoutStarted',
      secondary: [
        'Live Bootcamp PaymentReceived per exposed user',
        'Live Bootcamp revenue per exposed user',
        'Bootcamp alternate path selections per exposed user',
      ],
    },
    primaryMetric: 'Live Bootcamp checkout starts per exposed user',
    secondaryMetrics: [
      'Live Bootcamp PaymentReceived per exposed user',
      'Live Bootcamp revenue per exposed user',
      'Bootcamp alternate path selections per exposed user',
    ],
    startDate: '2026-08-05',
    status: 'draft',
    type: 'posthog_experiment',
    posthog_experiment_id: 402059,
    hypothesis: 'Agentic Trading Bootcamp will increase qualified live-course checkout starts versus AI Trading Bootcamp because agentic trading has stronger current category momentum.',
    notes: 'Draft in PostHog with a 50/50 control-agentic split, experience continuity, and test-account exclusion. Keep the flag inactive until this code is deployed. Direct review routes are /courses/ai-trading-bootcamp/control and /courses/ai-trading-bootcamp/agentic. Payment metrics filter plan_slug=ai-bootcamp; validate that exact property on the first settled live-bootcamp purchase before declaring a winner.',
  },

  MARKETPLACE_REDESIGN_2026_07: {
    name: 'marketplace_redesign_2026_07',
    featureFlag: 'marketplace-redesign-2026-07',
    description: 'Compare legacy marketplace with evidence-first strategy marketplace redesign.',
    variants: {
      control: 'Legacy marketplace',
      test: 'Evidence-first marketplace redesign',
    },
    metrics: ['bot_details_viewed', 'backtest_started', 'deployment_started'],
    posthog_metrics: {
      primary: 'bot_details_viewed',
      secondary: ['backtest_started', 'deployment_started'],
    },
    primaryMetric: 'bot_details_viewed',
    secondaryMetrics: ['backtest_started', 'deployment_started'],
    startDate: '2026-07-30',
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 393806,
    hypothesis: 'Evidence-first comparison will increase qualified strategy detail views and downstream backtest or deployment starts.',
    notes: '50/50 control-test split, 100% eligible traffic, test accounts excluded. Local preview supports marketplaceVariant=control|test.',
  },

  // Sales Page Layout Test
  SALES_PAGE_LAYOUT: {
    name: 'sales_page_layout_test',
    featureFlag: 'sales_page_variant_05', // This will be auto-generated by PostHog Experiment
    description: 'Testing new sales page layout (SalesPage05) vs original',
    variants: {
      control: 'Original sales page (SalesPage)',
      test: 'New layout with benefits section (SalesPage05)' // PostHog uses 'test' not 'variant'
    },
    metrics: ['lead_captured', 'begin_checkout', 'payment_success'],
    posthog_metrics: {
      primary: 'Lead', // Maps to lead_captured events
      secondary: ['InitiateCheckout', 'Purchase'] // Maps to begin_checkout and payment_success events
    },
    startDate: '2025-06-28',
    status: 'archived',
    type: 'posthog_experiment' // Mark this as a PostHog experiment
  },

  // ARCHIVED: Pre-AI paywall test completed - pre-AI flow is now the default
  SALES_PAGE_PAYWALL_PRE_AI: {
    name: 'sales_page_paywall_pre_ai_generation',
    featureFlag: 'sales_page_paywall_pre_ai_generation',
    description: 'Testing the paywall move before AI prompt generation vs the legacy sales page flow',
    variants: {
      control: 'Original sales page (SalesPage)',
      pre_ai_paywall: 'Contact capture with paywall before AI prompt (SalesPage06)',
    },
    metrics: ['begin_checkout', 'payment_success'],
    startDate: '2025-07-05',
    endDate: '2025-12-21',
    status: 'archived', // Test completed - pre-AI flow is now default
    type: 'posthog_experiment',
    result: 'Pre-AI flow became default. Now testing page designs via SALES_PAGE_DESIGN experiment.',
  },

  // Sales Page Design A/B/C Test
  // Tests 3 sales page variants: control (minimal), sales05 (animated + FAQs), sales06 (video + SparkleButton)
  SALES_PAGE_DESIGN: {
    name: 'sales_page_design_abc_test',
    featureFlag: 'sales_page_design_variant',
    description: 'Testing 3 sales page designs: minimal control, animated squares with FAQs (sales05), and video with SparkleButton (sales06)',
    variants: {
      control: 'Minimal sales page with hero + checkout',
      sales05: 'Animated floating squares, FAQs, trust badges',
      sales06: 'YouTube video demo, SparkleButton CTA, benefit variants',
    },
    metrics: ['sales_prompt_page_view', 'payment_success'],
    startDate: '2025-12-21',
    status: 'archived',
    type: 'posthog_experiment',
    hypothesis: 'Sales05 showed ~65% chance of outperforming control. Testing if sales06 (video) performs even better.',
    endDate: '2026-03-09',
    result: 'Control won decisively. Lead: control 26.59% vs sales05 21.50% vs sales06 9.68%. Purchase roughly tied between control (3.05%) and sales05 (3.00%). Sales06 performed poorly. 100% traffic rolled to control.',
  },

  // Sales Page Design v2 A/B/C Test
  // Tests control vs 2 new Nate designs: sales07 (AI assistant theme) and sales08 (paradigm shift + bonus offer)
  // New pages include "Start My Free Trial" CTA and interactive prompt guide lead magnet
  SALES_PAGE_DESIGN_V2: {
    name: 'sales_page_design_v2_abc_test',
    featureFlag: 'sales_page_design_v2_variant',
    description: 'Testing 3 sales page designs: minimal control vs AI assistant theme (sales07) vs paradigm shift with bonus offer (sales08). New pages feature free trial CTA and prompt guide lead magnet.',
    variants: {
      control: 'Minimal sales page with hero + checkout (unchanged)',
      sales07: 'AI Command Center theme, "Start My Free Trial" CTA, plain English focus',
      sales08: 'Paradigm shift theme, TrustStrip, bonus prompt guide offer, countdown timer',
    },
    metrics: ['Lead', 'Purchase', 'backtest_started', 'deployment_started'],
    posthog_metrics: {
      primary: ['Lead', 'Purchase'],
      secondary: ['backtest_started', 'deployment_started'],
    },
    startDate: '2026-03-09',
    status: 'archived',
    type: 'posthog_experiment',
    posthog_experiment_id: 360910,
    hypothesis: 'New page designs with explicit free trial messaging and interactive prompt guide lead magnet will outperform the minimal control on both Lead capture and Purchase conversion.',
    endDate: '2026-06-19',
    result: 'Superseded by the Sales Page Signup Framing v4 experiment on the current /sales surface. PostHog experiment 360910 is stopped; keep local registry archived so it is not treated as an active app experiment.',
  },

  // Sales Page Signup Framing v4
  // Three-way test between full-form, email-only, and product-led embedded signup.
  SALES_PAGE_SIGNUP_FRAMING_V4: {
    name: 'sales_page_signup_framing_v4',
    featureFlag: 'sales_page_signup_framing_v4_variant',
    description: 'Sales signup v4: full-form create-account control, email-only create-account, and a product-led auth-first sales page.',
    variants: {
      control: 'Sales07 create-account copy with full lead form',
      email_only: 'Sales07 create-account copy with email-only lead form',
      auth_first: 'Product-led auth overlay with embedded Google and email signup',
    },
    metrics: ['Lead', 'Purchase', 'PaymentReceived'],
    posthog_metrics: {
      primary: 'Lead',
      secondary: ['Purchase', 'PaymentReceived'],
    },
    startDate: '2026-06-30',
    endDate: '2026-07-27',
    status: 'completed',
    type: 'posthog_experiment',
    posthog_experiment_id: 380136,
    hypothesis: 'Testing a product-led embedded signup against email-only and full-form routes will identify the highest-intent sales entry path.',
    result: 'Invalid analytics contract: auth-first Lead events used email distinct IDs while browser exposures later identified canonical user IDs. PostHog could not join confirmed account creations to auth-first exposures. Exposure counts also triggered a sample-ratio-mismatch warning.',
  },

  // Sales Page Signup Framing v5
  // Clean two-way replacement after fixing canonical PostHog identity attribution.
  SALES_PAGE_SIGNUP_FRAMING_V5: {
    name: 'sales_page_signup_framing_v5',
    featureFlag: 'sales_page_signup_framing_v5_variant',
    description: 'Clean comparison of email-only lead capture against auth-first account creation after fixing server-side PostHog identity attribution.',
    variants: {
      control: 'Sales07 create-account copy with email-only lead form',
      auth_first: 'Product-led auth overlay with embedded Google and email signup',
    },
    metrics: ['Lead', 'PaymentReceived'],
    posthog_metrics: {
      primary: 'Lead',
      secondary: ['PaymentReceived'],
    },
    startDate: '2026-07-27',
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 389884,
    hypothesis: 'Auth-first account creation will increase canonical Lead conversion versus email-only capture without reducing confirmed paid revenue.',
    notes: '50/50 split. PostHog control key renders email-only; auth_first key renders the complete product-led sales treatment. Keep both treatments unchanged for the full experiment window. Test accounts excluded.',
  },

  // INVALID: the original run mislabeled HomeV3 as control, so it could not
  // measure either redesign against the established production homepage.
  HOMEPAGE_CONVERSION_V3_V4: {
    name: 'homepage_conversion_v3_v4',
    featureFlag: 'homepage-conversion-v3-v4',
    description: 'Testing the prompt-first HomeV3 experience against the broker-first HomeV4 experience on the public homepage.',
    variants: {
      control: 'HomeV3 prompt-first strategy builder',
      test: 'HomeV4 broker-first connection path',
    },
    metrics: ['Lead', 'GetStartedClick', 'InitiateCheckout', 'PaymentReceived'],
    posthog_metrics: {
      primary: 'Lead',
      secondary: ['GetStartedClick', 'InitiateCheckout', 'PaymentReceived'],
    },
    startDate: '2026-07-16',
    endDate: '2026-07-17',
    status: 'completed',
    type: 'posthog_experiment',
    posthog_experiment_id: 383991,
    hypothesis: 'Broker-first messaging will increase anonymous visitor lead conversion versus prompt-first messaging without reducing checkout starts or server-confirmed payments.',
    target_lift: {
      lead: 15,
      purchase: 0,
    },
    notes: 'Stopped and marked invalid in PostHog on 2026-07-17. Historical data is preserved; the obsolete feature flag is disabled.',
    result: 'Invalid design: control rendered HomeV3 instead of the established homepage. Final preserved snapshot: 14 V3 exposures, 12 V4 exposures, 1 vs 0 Leads, 1 vs 2 GetStartedClick events, and no checkout or payment events.',
  },

  // Preserved three-way homepage conversion test. Production root now owns
  // one homepage; /home-v3 and /home-v4 remain deterministic review routes.
  HOMEPAGE_CONTROL_V3_V4: {
    name: 'homepage_control_v3_v4',
    featureFlag: 'homepage-control-v3-v4',
    description: 'Testing the established HomeLovable homepage against the prompt-first HomeV3 and broker-first HomeV4 experiences on the public homepage.',
    variants: {
      control: 'Established HomeLovable production homepage',
      v3: 'HomeV3 prompt-first strategy builder',
      v4: 'HomeV4 broker-first connection path',
    },
    metrics: ['Lead', 'GetStartedClick', 'InitiateCheckout', 'PaymentReceived', 'PaymentSetupCompleted'],
    posthog_metrics: {
      primary: 'Lead',
      secondary: ['GetStartedClick', 'InitiateCheckout', 'PaymentReceived', 'PaymentSetupCompleted'],
    },
    startDate: '2026-07-17',
    endDate: '2026-07-30',
    status: 'completed',
    type: 'posthog_experiment',
    posthog_experiment_id: 384828,
    hypothesis: 'One of the conversion-focused redesigns will increase anonymous visitor lead conversion versus the established homepage without reducing checkout starts or server-confirmed payments.',
    target_lift: {
      lead: 15,
      purchase: 0,
    },
    notes: 'Stopped early in PostHog on 2026-07-30 without deleting or shipping a winner. Historical three-way run used 34% established control, 33% V3, 33% V4 with experience continuity enabled and test accounts excluded. PaymentSetupCompleted measured successful zero-dollar coupon or trial setup separately from collected PaymentReceived revenue.',
  },

  // Replacement four-way homepage conversion test.
  HOMEPAGE_CONTROL_V3_V4_CONTROL_ROOM: {
    name: 'homepage_control_v3_v4_control_room',
    featureFlag: 'homepage-control-v3-v4-control-room',
    description: 'Testing the established homepage, V3, V4, and the new Control Room homepage on the public root route.',
    variants: {
      control: 'Established HomeLovable production homepage',
      v3: 'HomeV3 prompt-first strategy builder',
      v4: 'HomeV4 broker-first connection path',
      control_room: 'New Control Room homepage',
    },
    metrics: ['Lead'],
    posthog_metrics: {
      primary: 'Lead',
      secondary: [],
    },
    startDate: '2026-07-30',
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 393334,
    hypothesis: 'Including the new Control Room homepage will increase qualified Lead conversion for anonymous homepage visitors without reducing checkout starts or completed payments.',
    notes: 'Launched in PostHog on 2026-07-30 after matching React routing reached production. Uses a 25/25/25/25 split, anonymous-to-auth experience continuity, and excludes internal/test users.',
  },

  // Homepage Trust Strip Test (High-Impact CRO Test)
  HOMEPAGE_TRUST_STRIP: {
    name: 'homepage_trust_strip_test',
    featureFlag: 'homepage-trust-strip-test',
    description: 'Testing homepage hero with trust strip (Trustpilot rating, strategy counter, security badges) vs original',
    variants: {
      control: 'Original homepage hero (no trust elements)',
      test: 'Homepage hero with trust strip (Trustpilot 4.6/5, 3000+ strategies counter, PCI-DSS & ISO badges)'
    },
    // 🚨 CRITICAL: PostHog experiment expects these EXACT event names
    metrics: ['GetStartedClick'], // PRIMARY: Main homepage CTA conversion metric
    posthog_metrics: {
      primary: 'GetStartedClick', // 🚨 CRITICAL: Must match PostHog experiment configuration
      secondary: ['Lead', 'TrialSignup', 'PageEngagement'] // Additional conversion metrics
    },
    startDate: '2025-06-28',
    status: 'completed',
    type: 'posthog_experiment',
    hypothesis: 'Adding trust signals (Trustpilot rating, live strategy counter, security badges) will increase hero CTA click-through by ≥20% and trial signups by ≥15% without increasing bounce rate',
    target_lift: {
      hero_cta_ctr: 20, // percentage
      trial_signups: 15, // percentage
      bounce_rate: 0 // no increase (guardrail)
    },
    sample_size_needed: '~18k visitors per variant for 95% confidence, 80% power',
    notes: 'The root route is owned by the Homepage V3 vs V4 conversion experiment; the old wrapper only remains on /home-old. The PostHog experiment was stopped and its feature flag deactivated on 2026-07-16.'
  },

  // AI Agent Welcome Prompts Test
  AI_AGENT_WELCOME_PROMPTS: {
    name: 'ai_agent_welcome_prompts_test',
    featureFlag: 'ai-agent-welcome-prompts',
    description: 'Testing AI Agent welcome screen. Comparing minimal (headline + tip only) vs text_examples (headline + tip + copy/paste example prompts). No clickable buttons in either variant.',
    variants: {
      control: 'control',      // Minimal: headline + tip only
      test: 'test'             // Text examples: headline + tip + example prompts
    },
    metrics: ['Purchase', 'BacktestStarted', 'DeploymentStarted'],
    posthog_metrics: {
      primary: 'Purchase',
      secondary: ['BacktestStarted', 'DeploymentStarted']
    },
    startDate: '2025-12-17',
    status: 'active',
    type: 'posthog_experiment',
    hypothesis: 'Text examples will improve first prompt quality and increase conversion rates (purchases, backtests, deployments) compared to minimal headline-only welcome screen.'
  },

  // Starter Pricing ABC Test - Testing price and trial variations
  // Goal: Improve trial-to-paid conversion (dropped from 15-20% to 0%)
  STARTER_PRICING_ABC: {
    name: 'starter_pricing_abc_test',
    featureFlag: 'starter-pricing-abc',
    description: 'ABC test for Starter plan pricing: control ($99+trial), no_trial ($99 upfront), discounttest-2 ($49 upfront with urgency)',
    variants: {
      control: '$99/month with 7-day free trial (current)',
      no_trial: '$99/month upfront - no trial',
      'discounttest-2': '$49/month upfront - 50% off limited time urgency',  // PostHog variant key
    },
    metrics: ['PricingPageView', 'PricingCTAClick', 'Purchase', 'TrialStarted'],
    posthog_metrics: {
      primary: 'Purchase',
      secondary: ['PricingCTAClick', 'TrialStarted']
    },
    startDate: '2026-01-15',
    endDate: '2026-04-22',
    status: 'archived',
    type: 'posthog_experiment',
    hypothesis: 'Hypothesis: Payment declines are from insufficient_funds (price sensitivity). Testing if removing trial friction ($99 upfront) or lowering price ($49) improves conversion. High-signal test due to low traffic.',
    target_lift: {
      purchase_rate: 50, // percentage improvement goal
    },
    notes: 'Discount variant requires Stripe coupon "STARTER50OFF" (50% off). No trial variants need trialDays=0 on Product entity in botspot_node.',
    result: 'ENDED 2026-04-22 to make way for LIGHT_VS_STARTER_CHECKOUT. discounttest-2 ($49 no-trial) won 2.47x vs no_trial ($99). PostHog engine numbers (2026-02-01 through 2026-04-20): no_trial 83 exposed / 2 purchases = 2.41%; discounttest-2 101 exposed / 6 purchases = 5.94%. Sample was tiny (8 total purchases) so PostHog flagged "not-enough-metric-data" - directional not statistically significant. $49 became the new control baseline for the successor LIGHT_VS_STARTER_CHECKOUT test.',
  },

  // Light vs Starter Checkout - Pure price test at the /checkout/starter-plan landing
  // page (the one users hit from the sales funnel). Replaces STARTER_PRICING_ABC.
  //
  // control: $99 strikethrough → $49/month (Starter, discounttest-2 winner of old test)
  // test:    $49 strikethrough → $24/month (Light, new lower tier)
  //
  // Both variants share identical UX: same 2-hour urgency timer, same 50%-off framing,
  // same "no trial" and "30-day money-back" copy, same CTA "Claim 50% Off Now".
  // The ONLY variable is the underlying product + price. Pure pricing experiment.
  //
  // Note: /checkout/light-plan (reached from Light CTA on /pricing) always shows Light
  // regardless of this flag — the user explicitly chose Light. This flag only governs
  // /checkout/starter-plan (the sales-funnel checkout surface).
  //
  // Primary metric: PaymentReceived (server-side, reliable) rather than Purchase
  // (client-side, currently lossy due to post-checkout hard redirect).
  LIGHT_VS_STARTER_CHECKOUT: {
    name: 'light_vs_starter_checkout',
    featureFlag: 'light-vs-starter-checkout',
    description: 'Pure price test on /checkout/starter-plan: $99→$49 Starter (control) vs $49→$24 Light (test). Same UX otherwise.',
    variants: {
      control: 'Starter $99 → $49 (discounttest-2 winner of previous test)',
      test: 'Light $49 → $24 (new lower tier)',
    },
    metrics: ['PaymentReceived', 'Purchase'],
    posthog_metrics: {
      primary: 'PaymentReceived',
      secondary: ['Purchase'],
    },
    startDate: '2026-04-22',
    status: 'planned',
    type: 'posthog_experiment',
    hypothesis: 'A $24 entry-level tier captures price-sensitive users who currently bounce at $49. Expected lift: 30-100% on PaymentReceived per exposed user. Cannibalization of Starter revenue is acceptable if Light stays meaningfully constrained on AI usage while still giving users one deployment to activate.',
    notes: 'Replaces STARTER_PRICING_ABC (which ran Feb-Apr 2026 and concluded: $49 no-trial won 2.47x vs $99 no-trial). Keep in draft until code deploys to prod, then launch. /checkout/light-plan is outside the experiment — pricing-page Light CTAs always route users to the Light variant by pathname. Light includes one deployment but materially lower AI/revision capacity than Starter.',
  },

  // Post-signup destination experiment. Assignment is evaluated only on the
  // dedicated /post-signup route after the authenticated attribution save has
  // been read back from the user profile. Landing pages never own this split.
  POST_SIGNUP_CHECKOUT_GATE_V1: {
    name: 'post_signup_checkout_gate_v1',
    featureFlag: 'post_signup_checkout_gate_v1_variant',
    description: 'Current post-signup destination versus immediate Starter/Light checkout after required self-reported attribution.',
    variants: {
      control: 'Continue to the existing chooser, saved prompt, handoff, or other intended product destination',
      test: 'Go immediately to the Starter/Light checkout experiment while preserving the intended destination',
    },
    metrics: ['PaymentReceived', 'Purchase'],
    posthog_metrics: {
      primary: 'PaymentReceived with is_first_payment=true per exposed user',
      secondary: [
        'Purchase per exposed user',
        'PaymentReceived revenue per exposed user',
        'InitiateCheckout per exposed user',
      ],
    },
    startDate: '2026-08-09',
    status: 'planned',
    type: 'posthog_experiment',
    hypothesis: 'Requiring payment immediately after signup and attribution will increase first positive Starter/Light payments versus the current product-choice path.',
    notes: 'Assignment happens only after user.howFoundUs is persisted. The Light-vs-Starter and checkout-design experiments remain nested inside checkout. Direct Challenge registrations are excluded.',
  },

  // Sales Choose Page Copy A/B Test
  // Tests two copy variants on the /sales-choose fork page
  SALES_CHOOSE_COPY: {
    name: 'sales_choose_copy_test',
    featureFlag: 'sales_choose_copy',
    description: 'Testing action-oriented vs benefit-focused copy on the sales choose page fork',
    variants: {
      control: 'Action-oriented: "Pick a proven strategy" / "Build my own strategy"',
      benefit_focus: 'Benefit-focused: "Start with a winning strategy" / "Create something custom"',
    },
    metrics: ['sales_choose_path_selected', 'Purchase'],
    posthog_metrics: {
      primary: 'sales_choose_path_selected',
      secondary: ['Purchase'],
    },
    startDate: '2026-04-09',
    status: 'active',
    type: 'posthog_experiment',
    hypothesis: 'Benefit-focused copy will increase marketplace path selection by highlighting outcomes over actions.',
  },

  // Navbar Redesign
  // Tests current global navigation against a cleaner design-system treatment.
  // Planned replacement: NAVBAR_REVENUE_IA_2026_08 (Agent / Marketplace / Education IA).
  // When that ships, stop PostHog experiment 380364 and point App.tsx at the new registry entry.
  NAVBAR_REDESIGN: {
    name: 'navbar_redesign',
    featureFlag: 'navbar_redesign_variant',
    description: 'Testing a cleaner token-based navbar treatment. The test variant now carries the revenue IA (Agent, Marketplace, Education) while control keeps the legacy header.',
    variants: {
      control: 'Current navbar',
      test: 'Revenue IA navbar: Agent, Marketplace, Education, demoted Library items',
    },
    metrics: ['ai_agent_stream_started'],
    posthog_metrics: {
      primary: 'ai_agent_stream_started',
      secondary: ['backtest_started', 'deployment_started', 'InitiateCheckout', 'PaymentReceived'],
    },
    startDate: '2026-07-01',
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 380364,
    hypothesis: 'A quieter navbar with clearer primary and secondary actions will increase AI Agent activation without reducing checkout or paid conversion.',
    target_lift: {
      ai_agent_stream_started: 15,
      payment_received: 0,
    },
    notes: 'Launched in PostHog on 2026-07-02 with 50% control and 50% test allocation. Local preview: ?navbar_redesign_variant=test|control, ?navbarVariant=test|control, or ?navbar_revenue_ia_2026_08_variant=test|control on localhost/dev. When NAVBAR_REVENUE_IA_2026_08 ships, stop PostHog experiment 380364.',
  },

  // Planned Navbar Revenue IA replacement (draft; no PostHog ID yet).
  // App still evaluates NAVBAR_REDESIGN / navbar_redesign_variant for assignment.
  // Local overrides already accept navbar_revenue_ia_2026_08_variant without breaking
  // the older navbar_redesign_variant / navbarVariant preview keys.
  NAVBAR_REVENUE_IA_2026_08: {
    name: 'navbar_revenue_ia_2026_08',
    featureFlag: 'navbar_revenue_ia_2026_08_variant',
    description: 'Planned replacement for navbar_redesign. Primary IA is Agent, Marketplace, and Education, with Library (Strategies, Backtests, Bots, Accounts) and Dashboard demoted out of the leftmost bar.',
    variants: {
      control: 'Legacy or prior redesign control',
      test: 'Revenue IA: Agent leftmost, Marketplace, Education dropdown, Dashboard after login, Library under Account',
    },
    metrics: ['ai_agent_stream_started'],
    posthog_metrics: {
      primary: 'ai_agent_stream_started',
      secondary: ['backtest_started', 'deployment_started', 'InitiateCheckout', 'PaymentReceived'],
    },
    startDate: '2026-08-08',
    status: 'draft',
    type: 'posthog_experiment',
    hypothesis: 'Putting Agent and Marketplace first with Education as a top-level dropdown will lift agent activation and education discovery versus burying Library items and Education under Explore.',
    notes: 'Draft/planned only. Do not invent a PostHog experiment ID until created in PostHog. Keep local preview working via ?navbar_revenue_ia_2026_08_variant=test|control and the existing navbar_redesign_variant / navbarVariant aliases. When this ships, stop old PostHog experiment 380364 (navbar_redesign) and switch App.tsx useTypedABTest to this registry entry.',
  },

  // Checkout Redesign
  // Tests presentation/layout across /checkout/starter-plan, /checkout/light-plan,
  // and the unified cart /checkout route used by Pro, All Access, and cart-based offers.
  // Product, price, coupon, Stripe mode, and post-purchase routing remain controlled
  // by the shared checkout code and Light-vs-Starter price experiment.
  CHECKOUT_REDESIGN_2026_07: {
    name: 'checkout_redesign_2026_07',
    featureFlag: 'checkout-redesign-2026-07',
    description: 'Testing current checkout against a quieter order-summary-first checkout layout on Starter, Light, and unified cart checkout routes.',
    variants: {
      control: 'Current checkout',
      test: 'Quiet checkout with compact order summary and payment-first form',
    },
    metrics: [
      'Purchase',
      'PaymentReceived',
      'PaymentReceived amount_usd',
      'PaymentSetupCompleted',
      'PaymentFailed',
      'stripe_confirm_failed',
    ],
    posthog_metrics: {
      primary: ['Purchase per exposed user', 'PaymentReceived per exposed user'],
      secondary: [
        'PaymentReceived revenue per exposed user',
        'InitiateCheckout to Purchase',
        'InitiateCheckout to PaymentReceived',
        'PaymentSetupCompleted per exposed user',
        'PaymentFailed per exposed user',
        'Stripe confirm failure per exposed user',
      ],
    },
    startDate: '2026-07-08',
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 381181,
    hypothesis: 'A less cluttered checkout with the price summary first and cleaner payment hierarchy will increase paid checkout conversion without changing price, product, or cart semantics.',
    target_lift: {
      payment_received: 20,
    },
    notes: 'Launched in PostHog on 2026-07-08 with 50% control and 50% test allocation. Metrics use exposed-user paid conversion, server-side PaymentReceived confirmation, revenue per exposed user via amount_usd, and payment failure guardrails.',
  },

  // Pricing Page Redesign
  // Presentation-only test. Product catalog, price, checkout routing, coupons,
  // and downstream checkout experiments remain unchanged between variants.
  PRICING_PAGE_REDESIGN_2026_07: {
    name: 'pricing_page_redesign_2026_07',
    featureFlag: 'pricing-page-redesign-2026-07',
    description: 'Testing the current pricing page against an outcome-first layout with a compact capacity and support comparison.',
    variants: {
      control: 'Current pricing page',
      test: 'Outcome-first pricing page with compact capacity comparison and separated training and enterprise offers',
    },
    metrics: ['PaymentReceived', 'pricing_cta_click', 'PaymentFailed', 'stripe_confirm_failed'],
    posthog_metrics: {
      primary: 'PaymentReceived per exposed user',
      secondary: [
        'PaymentReceived revenue per exposed user',
        'pricing_cta_click per exposed user',
        'Plan mix by PaymentReceived product',
        'PaymentFailed per exposed user',
        'Stripe confirm failure per exposed user',
      ],
    },
    startDate: '2026-07-14',
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 383736,
    hypothesis: 'Clear workload limits and stronger plan differentiation will increase paid conversion without reducing revenue per exposed user.',
    target_lift: {
      payment_received: 15,
      revenue_per_exposed_user: 0,
    },
    notes: 'Launched in PostHog on 2026-07-14 with 50% control and 50% test allocation across eligible traffic. Do not change price, product, coupon, or checkout targeting as part of this experiment.',
  },

  // Superseded CTA-only Google Search landing page experiment.
  GOOGLE_SEARCH_LP_V1: {
    name: 'google_search_lp_v1',
    featureFlag: 'google_search_lp_v1_variant',
    description: 'CTA copy A/B on the dedicated Google Search landing page for Schwab and thinkorswim Search ads.',
    variants: {
      control: 'TRY THIS FREE CTA with job-matched proof bullets',
      create_account_cta: 'CREATE ACCOUNT CTA with the same job-matched page',
    },
    metrics: ['Lead', 'PaymentReceived'],
    posthog_metrics: {
      primary: 'Lead',
      secondary: ['PaymentReceived'],
    },
    startDate: '2026-08-08',
    status: 'completed',
    type: 'local_sticky_experiment',
    hypothesis: 'A lower-friction TRY THIS FREE CTA will lift Lead conversion versus CREATE ACCOUNT on cold Schwab Search traffic without reducing PaymentReceived.',
    notes: 'Superseded on 2026-08-09 by GOOGLE_SEARCH_SIGNUP_EXPERIENCE_V1. This CTA-only local split must not run at the same time as the signup-experience experiment.',
  },

  // Dedicated Google Search paid LP signup-experience A/B on /lp/google-search.
  GOOGLE_SEARCH_SIGNUP_EXPERIENCE_V1: {
    name: 'google_search_signup_experience_v1',
    featureFlag: 'google_search_signup_experience_v1_variant',
    description: 'Email capture control versus the product-led embedded auth-first experience across all four job-matched Google Search landing pages.',
    variants: {
      control: 'Current job-matched page with email-only TRY THIS FREE form',
      auth_first: 'Martin-style split-screen page with job-matched promise and embedded Google or email account creation',
    },
    metrics: ['Lead', 'PaymentReceived'],
    posthog_metrics: {
      primary: 'Lead',
      secondary: ['PaymentReceived'],
    },
    startDate: '2026-08-09',
    status: 'active',
    type: 'posthog_experiment',
    posthog_experiment_id: 409115,
    hypothesis: 'The product-led auth-first experience will increase canonical Lead conversion from cold job-matched Google Search traffic versus email capture alone.',
    notes: 'Pooled 50/50 experiment across schwab-ai-control, schwab-copilot, tos-scanner, and portfolio-monitor. Preserve google_search_lp_job as a diagnostic breakdown. Do not change ads, keywords, budgets, bidding, or networks during the observation window.',
  },

  // Add future experiments here...
  // CHECKOUT_FLOW: {
  //   name: 'checkout_flow_test',
  //   featureFlag: 'checkout_flow_variant',
  //   description: 'Testing simplified checkout flow',
  //   variants: {
  //     control: 'Multi-step checkout',
  //     variant: 'Single-page checkout'
  //   },
  //   metrics: ['checkout_started', 'checkout_completed'],
  //   startDate: '2025-07-01',
  //   status: 'planned'
  // }
};

// Experiment Status Types
export const EXPERIMENT_STATUS = {
  PLANNED: 'planned',
  ACTIVE: 'active',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  ARCHIVED: 'archived'
} as const;

// Helper function to get experiment configuration
export const getExperiment = (experimentKey: string): ExperimentConfig | undefined => {
  return ACTIVE_EXPERIMENTS[experimentKey];
};

// Helper function to get all active experiments
export const getActiveExperiments = (): ExperimentRegistry => {
  return Object.entries(ACTIVE_EXPERIMENTS)
    .filter(([, experiment]) => experiment.status === EXPERIMENT_STATUS.ACTIVE)
    .reduce<ExperimentRegistry>((acc, [key, experiment]) => {
      acc[key] = experiment;
      return acc;
    }, {});
};

// Helper function to track experiment conversion
export const trackExperimentConversion = (
  experimentName: string,
  variant: string,
  conversionEvent: string,
  additionalData: Record<string, unknown> = {},
): Record<string, unknown> => {
  console.log(`🎯 [Experiment Conversion] ${experimentName} - ${variant} - ${conversionEvent}`, additionalData);
  
  // This could be enhanced to send to your analytics system
  return {
    experiment: experimentName,
    variant,
    conversion: conversionEvent,
    timestamp: new Date().toISOString(),
    ...additionalData
  };
};

/**
 * Usage Examples:
 * 
 * 1. Basic A/B Test in a Component (PostHog Experiment):
 * ```javascript
 * import useABTest from '../hooks/useABTest';
 * import { ACTIVE_EXPERIMENTS } from '../utils/experiments';
 * 
 * const MyComponent = () => {
 *   const experiment = ACTIVE_EXPERIMENTS.SALES_PAGE_LAYOUT;
 *   const { variant, isLoading, isTest, isControl } = useABTest(
 *     experiment.name,
 *     experiment.featureFlag,
 *     'control'
 *   );
 *   
 *   if (isLoading) return <Loading />;
 *   
 *   return isTest ? <NewComponent /> : <OriginalComponent />;
 *   // OR: return variant === 'test' ? <NewComponent /> : <OriginalComponent />;
 * };
 * ```
 * 
 * 2. Conditional Rendering (PostHog Experiment):
 * ```javascript
 * const { isTest } = useABTest('my_test', 'my_flag');
 * 
 * return (
 *   <div>
 *     {isTest && <NewFeature />}
 *     <ExistingContent />
 *   </div>
 * );
 * ```
 * 
 * 3. String-based Variants:
 * ```javascript
 * // PostHog flag returns: "red", "blue", or "green"
 * const { variant } = useABTest('button_color_test', 'button_color_flag', 'red');
 * 
 * return <Button color={variant}>Click me</Button>;
 * ```
 */ 
